import { FC, memo, useCallback } from 'react'
import styles from './Text.module.scss'

const titleSizes = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
} as const

const paragraphSizes = {
  m: 'm',
  s: 's',
  xs: 'xs',
} as const

type TitleSize = ObjectValues<typeof titleSizes>
type ParagraphSize = ObjectValues<typeof paragraphSizes>

interface TextProps {
  title?: string
  subtitle?: string
  paragraph?: string
  titleSize?: TitleSize
  paragraphSize?: ParagraphSize
  className?: string
}

const Text: FC<TextProps> = memo((props) => {
  const {
    title = null,
    subtitle = null,
    paragraph = null,
    titleSize = 'h3',
    paragraphSize = 'm',
    className = '',
  } = props

  const getClassNames = useCallback(
    (newClassName: string) => {
      const classes = []
      if (newClassName) {
        classes.push(newClassName)
      }
      if (className) {
        classes.push(className)
      }
      return classes.join(' ')
    },
    [className]
  )

  if (title) {
    switch (titleSize) {
      case 'h1':
        return <h1 className={getClassNames(styles.title_h1)}>{title}</h1>
      case 'h2':
        return <h2 className={getClassNames(styles.title_h2)}>{title}</h2>
      case 'h3':
        return <h3 className={getClassNames(styles.title_h3)}>{title}</h3>
      case 'h4':
        return <h4 className={getClassNames(styles.title_h4)}>{title}</h4>
      case 'h5':
        return <h5 className={getClassNames(styles.title_h5)}>{title}</h5>
      case 'h6':
        return <h6 className={getClassNames(styles.title_h6)}>{title}</h6>
      default:
        return <h3 className={getClassNames(styles.title_h3)}>{title}</h3>
    }
  }

  if (subtitle) {
    return <p className={getClassNames(styles.subtitle)}>{subtitle}</p>
  }

  if (paragraph) {
    switch (paragraphSize) {
      case 'm':
        return <p className={getClassNames(styles.paragraph_m)}>{paragraph}</p>
      case 's':
        return <p className={getClassNames(styles.paragraph_s)}>{paragraph}</p>
      case 'xs':
        return <p className={getClassNames(styles.paragraph_xs)}>{paragraph}</p>
      default:
        return <p className={getClassNames(styles.paragraph_m)}>{paragraph}</p>
    }
  }

  return <p className={getClassNames(styles.paragraph_m)}>{paragraph}</p>
})

export default Text
